import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  ShieldCheck, Crown, Users, UserMinus, Wrench, Search, Loader2,
  Pencil, Ban, History, AlertTriangle, Shield, FileText, CheckCircle2, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const ROLE_COLORS = {
  superuser: 'bg-violet-100 text-violet-800 border-violet-200',
  admin: 'bg-blue-100 text-blue-800 border-blue-200',
  hr_admin: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  hr_viewer: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  manager: 'bg-amber-100 text-amber-800 border-amber-200',
  staff: 'bg-slate-100 text-slate-700 border-slate-200',
};

const fmtDate = (s) => (s ? new Date(s).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

/**
 * Page-access compliance matrix.
 *
 * Renders a giant table (users × pages). Green tick = allowed, red cross =
 * denied. Used for ICO compliance — at any moment the superuser can produce a
 * snapshot of who can access which page. Pages are grouped by sidebar group so
 * the table reads the same way the user sees them.
 */
function PageAccessMatrix({ registry, matrix, filterUsers, canEdit, toggleCell, savingCell }) {
  const [groupFilter, setGroupFilter] = useState('all');

  const groups = React.useMemo(() => {
    const out = [];
    registry.forEach((p) => { if (!out.includes(p.group)) out.push(p.group); });
    return out;
  }, [registry]);

  const visiblePages = React.useMemo(() => {
    if (groupFilter === 'all') return registry;
    return registry.filter((p) => p.group === groupFilter);
  }, [registry, groupFilter]);

  const visibleUsers = filterUsers(matrix || []);

  // Totals for the header summary — "X of Y users can see this page"
  const pageTotals = React.useMemo(() => {
    const t = {};
    visiblePages.forEach((p) => {
      t[p.path] = visibleUsers.reduce((n, u) => n + ((u.access || {})[p.path] !== false ? 1 : 0), 0);
    });
    return t;
  }, [visiblePages, visibleUsers]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" /> Who can access which page
            </CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">
              {canEdit
                ? 'Live access per user. Click any tick/cross to grant or remove that page for that user — every change is recorded in Change History.'
                : 'Live snapshot resolved from role defaults + per-user overrides. Use this for ICO compliance audits.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setGroupFilter('all')}
              className={`px-2.5 py-1 rounded text-xs font-medium border transition-all ${groupFilter === 'all' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
              data-testid="page-group-filter-all"
            >
              All groups
            </button>
            {groups.map((g) => (
              <button
                key={g}
                onClick={() => setGroupFilter(g)}
                className={`px-2.5 py-1 rounded text-xs font-medium border transition-all ${groupFilter === g ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                data-testid={`page-group-filter-${g.toLowerCase()}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-xs border-collapse" data-testid="page-access-matrix">
          <thead>
            <tr>
              <th className="text-left py-2 pr-3 sticky left-0 bg-white border-b border-slate-200 z-10 min-w-[180px]">User</th>
              {visiblePages.map((p) => (
                <th
                  key={p.path}
                  className="px-1.5 py-2 border-b border-slate-200 align-bottom"
                  title={`${p.label} — ${p.path}`}
                >
                  <div className="flex flex-col items-center justify-end gap-0.5 min-w-[42px]">
                    <span className="text-[10px] text-slate-500 leading-tight text-center" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 90, fontWeight: 500 }}>
                      {p.label}
                    </span>
                    <span className="text-[9px] text-slate-400">{pageTotals[p.path]}/{visibleUsers.length}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleUsers.length === 0 ? (
              <tr><td colSpan={visiblePages.length + 1} className="text-center py-6 text-slate-400 italic">No users match the current filter.</td></tr>
            ) : visibleUsers.map((u) => (
              <tr key={u.user_id} className="hover:bg-slate-50/60" data-testid={`page-matrix-row-${u.user_id}`}>
                <td className="py-1.5 pr-3 sticky left-0 bg-white border-b border-slate-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{u.name || u.email}</p>
                      <p className="text-[10px] text-slate-500 truncate">{u.email}</p>
                    </div>
                    <Badge className={`${ROLE_COLORS[u.role] || ROLE_COLORS.staff} border text-[9px] px-1 flex-shrink-0`}>{u.role}</Badge>
                  </div>
                </td>
                {visiblePages.map((p) => {
                  const allowed = (u.access || {})[p.path] !== false;
                  const cellKey = `${u.user_id}|${p.path}`;
                  const saving = savingCell === cellKey;
                  const editable = canEdit && u.role !== 'superuser';
                  const icon = saving
                    ? <Loader2 className="w-4 h-4 animate-spin text-slate-400 inline-block" />
                    : allowed
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500 inline-block" />
                      : <XCircle className="w-4 h-4 text-rose-500 inline-block" />;
                  return (
                    <td
                      key={p.path}
                      className="text-center px-1 py-1.5 border-b border-slate-100"
                      title={editable
                        ? `${u.name || u.email} · ${p.label} — click to ${allowed ? 'REMOVE' : 'GRANT'} access`
                        : `${u.name || u.email} · ${p.label} (${p.path}) · ${allowed ? 'allowed' : 'denied'}`}
                      data-testid={`page-cell-${u.user_id}-${p.path.replace(/\//g, '-').slice(1) || 'root'}`}
                    >
                      {editable ? (
                        <button
                          type="button"
                          onClick={() => toggleCell(u, p.path)}
                          disabled={saving}
                          className="inline-flex items-center justify-center rounded p-0.5 transition-colors hover:bg-slate-100 hover:ring-1 hover:ring-slate-300 disabled:opacity-50 cursor-pointer"
                          data-testid={`page-toggle-${u.user_id}-${p.path.replace(/\//g, '-').slice(1) || 'root'}`}
                        >
                          {icon}
                        </button>
                      ) : icon}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[10px] text-slate-400 mt-3">
          {canEdit
            ? <>Tip: click any cell to toggle that page for that user. Superuser accounts always have full access and can't be changed here.</>
            : <>Tip: each cross-cell is a denied page. Click <strong>Access Control</strong> in the sidebar to change a user's overrides — every flip is recorded in <em>Change History</em> for ICO compliance.</>}
        </p>
      </CardContent>
    </Card>
  );
}

export const AccessAudit = () => {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('overview');
  const [savingCell, setSavingCell] = useState(null);

  const canEdit = me?.role === 'superuser';

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/access/audit');
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load audit');
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  // Toggle a single page for a user straight from the matrix. We send the
  // user's FULL current access map (with this one path flipped) because the
  // backend rebuilds the override from exactly the paths it receives — sending
  // only one path would wipe the user's other customisations.
  const toggleCell = async (u, path) => {
    if (!canEdit || u.role === 'superuser') return;
    const current = (u.access || {})[path] !== false;
    const next = !current;
    const cellKey = `${u.user_id}|${path}`;
    const registry = data?.page_registry || [];
    const fullAccess = {};
    registry.forEach((p) => { fullAccess[p.path] = (u.access || {})[p.path] !== false; });
    fullAccess[path] = next;

    const applyAccess = (val) => setData((prev) => ({
      ...prev,
      page_matrix: (prev.page_matrix || []).map((row) =>
        row.user_id === u.user_id ? { ...row, access: { ...row.access, [path]: val } } : row),
    }));

    setSavingCell(cellKey);
    applyAccess(next); // optimistic
    try {
      await api.post(`/permissions/pages/users/${u.user_id}`, { page_access: fullAccess });
      const label = (registry.find((p) => p.path === path) || {}).label || path;
      toast.success(`${next ? 'Granted' : 'Removed'} "${label}" for ${u.name || u.email}`);
    } catch (e) {
      applyAccess(current); // revert
      toast.error(e.response?.data?.detail || 'Could not update access');
    } finally {
      setSavingCell(null);
    }
  };

  const revokeAll = async (user) => {
    if (user.id === me?.id) { toast.error('Cannot revoke your own access'); return; }
    const phrase = `REVOKE ${user.email}`;
    const answer = window.prompt(
      `This will:\n  • Set role=staff and is_admin=false\n  • Clear HR role\n  • Set active=false (cannot log in)\n  • Deactivate any linked engineer account\n  • Remove from Meta Leads access\n\nType:  ${phrase}\nto confirm.`,
    );
    if (answer !== phrase) { toast('Cancelled'); return; }
    try {
      const r = await api.post(`/admin/access/revoke-all/${user.id}`);
      toast.success(
        `Access revoked${r.data?.engineer_accounts_deactivated ? ` (+ ${r.data.engineer_accounts_deactivated} engineer account)` : ''}`,
      );
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revoke failed');
    }
  };

  const filterUsers = (list) => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((u) =>
      (u.email || '').toLowerCase().includes(q)
      || (u.name || '').toLowerCase().includes(q),
    );
  };

  const Section = ({ title, icon: Icon, color, users, kind = 'user' }) => {
    const filtered = filterUsers(users || []);
    return (
      <Card data-testid={`access-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Icon className={`w-4 h-4 ${color}`} />
            {title}
          </CardTitle>
          <Badge variant="outline" className="text-[10px]">{filtered.length}{filtered.length !== (users?.length || 0) ? ` of ${users?.length}` : ''}</Badge>
        </CardHeader>
        <CardContent className="space-y-1.5 pt-1">
          {filtered.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-2">No users.</p>
          ) : filtered.map((u) => (
            <div key={u.id} className="flex items-center justify-between gap-2 p-2 rounded border border-slate-100 hover:bg-slate-50 text-sm" data-testid={`user-row-${u.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-slate-900 truncate">{u.name || u.email}</p>
                  {kind === 'user' && (
                    <>
                      <Badge className={`${ROLE_COLORS[u.role] || ROLE_COLORS.staff} border text-[10px] px-1.5`}>{u.role}</Badge>
                      {u.hr_role && <Badge className={`${ROLE_COLORS[u.hr_role] || ROLE_COLORS.staff} border text-[10px] px-1.5`}>HR · {u.hr_role.replace('_', ' ')}</Badge>}
                      {!u.active && <Badge className="bg-rose-100 text-rose-700 border-rose-200 border text-[10px] px-1.5">Inactive</Badge>}
                    </>
                  )}
                  {kind === 'engineer' && (
                    <Badge className="bg-cyan-100 text-cyan-800 border-cyan-200 border text-[10px] px-1.5">Engineer</Badge>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                {kind === 'user' && u.access_updated_at && (
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Last changed {fmtDate(u.access_updated_at)}{u.access_updated_by ? ` · by ${u.access_updated_by}` : ''}
                  </p>
                )}
              </div>
              {kind === 'user' && u.id !== me?.id && (
                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => navigate(`/admin/users?focus=${u.id}`)}
                    data-testid={`edit-${u.id}`}
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    onClick={() => revokeAll(u)}
                    data-testid={`revoke-${u.id}`}
                  >
                    <Ban className="w-3 h-3 mr-1" /> Revoke
                  </Button>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></Layout>;
  }
  if (!data) return <Layout><p className="p-8 text-slate-500">Could not load audit.</p></Layout>;

  const totals = data.totals || {};
  const groups = data.groups || {};

  return (
    <Layout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="access-audit-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 font-['Manrope']">
              <ShieldCheck className="w-6 h-6 text-blue-600" />
              Access Audit
            </h1>
            <p className="text-sm text-slate-500 mt-1">Who has elevated access and when it was last changed. Use this when onboarding/offboarding staff.</p>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email…"
              className="pl-9 w-64"
              data-testid="access-audit-search"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            ['Superusers', totals.superusers, 'text-violet-600'],
            ['Admins', totals.admins, 'text-blue-600'],
            ['HR Admins', totals.hr_admins, 'text-emerald-600'],
            ['HR Viewers', totals.hr_viewers, 'text-emerald-500'],
            ['Managers', totals.managers, 'text-amber-600'],
            ['Engineers', totals.engineers, 'text-cyan-600'],
          ].map(([label, value, color]) => (
            <Card key={label} className="border-slate-200">
              <CardContent className="p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
                <p className={`text-2xl font-bold ${color}`}>{value || 0}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {totals.inactive > 0 && (
          <Card className="border-rose-200 bg-rose-50/30">
            <CardContent className="p-3 flex items-center justify-between">
              <p className="text-sm text-rose-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600" />
                {totals.inactive} inactive {totals.inactive === 1 ? 'account' : 'accounts'} — review below.
              </p>
            </CardContent>
          </Card>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages">
              <FileText className="w-3.5 h-3.5 mr-1" /> Page Access
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Change History</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Section title="Superusers" icon={Crown} color="text-violet-500" users={groups.superusers} />
              <Section title="Admins" icon={Shield} color="text-blue-500" users={groups.admins} />
              <Section title="HR Admins" icon={ShieldCheck} color="text-emerald-600" users={groups.hr_admins} />
              <Section title="HR Viewers" icon={Users} color="text-emerald-500" users={groups.hr_viewers} />
              <Section title="Managers" icon={Users} color="text-amber-600" users={groups.managers} />
              <Section title="Engineers" icon={Wrench} color="text-cyan-600" users={groups.engineers} kind="engineer" />
              {totals.inactive > 0 && (
                <Section title="Inactive (No Access)" icon={UserMinus} color="text-rose-500" users={groups.inactive} />
              )}
            </div>
          </TabsContent>

          <TabsContent value="pages" className="mt-4">
            <PageAccessMatrix
              registry={data.page_registry || []}
              matrix={data.page_matrix || []}
              filterUsers={filterUsers}
              canEdit={canEdit}
              toggleCell={toggleCell}
              savingCell={savingCell}
            />
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4" /> Recent Access Changes</CardTitle>
                <p className="text-xs text-slate-500">Last 100 role / status / page-access changes, most recent first. Append-only — kept for ICO compliance.</p>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {(data.recent_changes || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-2">No changes recorded yet.</p>
                ) : (data.recent_changes || []).map((entry) => {
                  const isPageRole = entry.action === 'page_access_role';
                  const isPageUser = entry.action === 'page_access_user' || entry.action === 'page_access_user_reset';
                  const label = entry.user_name || entry.user_email || (isPageRole ? `Role: ${entry.role}` : '—');
                  return (
                    <div key={entry.id} className="p-2.5 rounded border border-slate-100 hover:bg-slate-50 text-xs" data-testid={`audit-row-${entry.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                            {entry.action === 'revoke_all' && <Badge className="bg-rose-100 text-rose-700 border-rose-200 border text-[10px]">Revoke All</Badge>}
                            {isPageRole && <Badge className="bg-violet-100 text-violet-700 border-violet-200 border text-[10px]">Role Page Access</Badge>}
                            {entry.action === 'page_access_user' && <Badge className="bg-blue-100 text-blue-700 border-blue-200 border text-[10px]">User Page Access</Badge>}
                            {entry.action === 'page_access_user_reset' && <Badge className="bg-amber-100 text-amber-700 border-amber-200 border text-[10px]">Reset to Role Default</Badge>}
                            {label}
                          </p>
                          {entry.user_email && <p className="text-[11px] text-slate-500">{entry.user_email}</p>}
                        </div>
                        <p className="text-[10px] text-slate-400 flex-shrink-0">{fmtDate(entry.changed_at)}</p>
                      </div>
                      <div className="mt-1.5 ml-1 pl-2 border-l-2 border-slate-200 space-y-0.5">
                        {Object.entries(entry.changes || {}).map(([field, ch]) => {
                          // Page-access changes store { from: bool, to: bool }; legacy entries store other shapes
                          const isPageChange = isPageRole || isPageUser;
                          const fromVal = ch.from;
                          const toVal = ch.to;
                          return (
                            <p key={field} className="text-[11px] text-slate-700">
                              <span className="font-mono text-slate-500">{field}</span>:{' '}
                              {isPageChange ? (
                                <>
                                  {fromVal ? (
                                    <span className="text-emerald-600 inline-flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> allowed</span>
                                  ) : (
                                    <span className="text-rose-600 inline-flex items-center gap-0.5"><XCircle className="w-3 h-3" /> denied</span>
                                  )}
                                  {' → '}
                                  {toVal ? (
                                    <span className="font-semibold text-emerald-700 inline-flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> allowed</span>
                                  ) : (
                                    <span className="font-semibold text-rose-700 inline-flex items-center gap-0.5"><XCircle className="w-3 h-3" /> denied</span>
                                  )}
                                </>
                              ) : (
                                <>
                                  <span className="line-through text-slate-400">{String(fromVal ?? '—')}</span>
                                  {' → '}
                                  <span className="font-semibold">{String(toVal ?? '—')}</span>
                                </>
                              )}
                            </p>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">by {entry.changed_by_name}</p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AccessAudit;
